<script>
  import { workspace, callBbAi, refreshWorkspace } from '../lib/stores/workspace.js';
  import { supabase } from '../lib/supabase.js';
  import { showToast } from '../lib/stores/toast.js';

  let { initialAudienceId = null, initialDealId = null, onClose, onChainCreated } = $props();

  // Step state derived from props
  let audienceId = $state(initialAudienceId);
  let dealId = $state(initialDealId);
  let manualSteps = $state([]); // [{ name, description, existing_id? }]
  let stepDraft = $state({ name: '', description: '' });
  let existingStepId = $state('');

  const availableExistingSteps = $derived(
    (workspace.steps || []).filter(s => !s.deleted_at && !manualSteps.some(m => m.existing_id === s.id))
  );

  function addExistingStep() {
    if (!existingStepId) return;
    const s = (workspace.steps || []).find(x => x.id === existingStepId);
    if (!s) return;
    manualSteps = [...manualSteps, {
      name: s.step_name || 'Untitled step',
      description: s.step_description || '',
      existing_id: s.id
    }];
    existingStepId = '';
  }

  let pairLoading = $state(false);
  let buildLoading = $state(false);
  let errorMsg = $state(null);

  const step = $derived.by(() => {
    if (audienceId && dealId) return 3;
    if (audienceId || dealId) return 2;
    return 1;
  });

  const audience = $derived(workspace.audiences.find(a => a.id === audienceId) || null);
  const deal = $derived(workspace.deals.find(d => d.id === dealId) || null);

  function pickAudience(id) { audienceId = id; }
  function pickDeal(id) { dealId = id; }

  async function aiGenerateOtherSide() {
    pairLoading = true; errorMsg = null;
    try {
      const payload = { workspace_id: workspace.id };
      if (audienceId) payload.audience_id = audienceId;
      else if (dealId) payload.deal_id = dealId;
      const r = await callBbAi('pair_segment', payload);
      if (r?.error) throw new Error(r.detail?.message || r.error);
      await refreshWorkspace();
      // Pick up the newly created complement
      if (audienceId && r?.deal_id) dealId = r.deal_id;
      else if (dealId && r?.audience_id) audienceId = r.audience_id;
      showToast('AI generated the complement segment', 'success');
    } catch (e) {
      errorMsg = e.message;
      showToast('Generate failed: ' + e.message, 'error');
    } finally {
      pairLoading = false;
    }
  }

  function addManualStep() {
    if (!stepDraft.name.trim()) return;
    manualSteps = [...manualSteps, { name: stepDraft.name.trim(), description: stepDraft.description.trim() }];
    stepDraft = { name: '', description: '' };
  }
  function removeStep(i) {
    manualSteps = manualSteps.filter((_, idx) => idx !== i);
  }
  function moveStep(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= manualSteps.length) return;
    const next = [...manualSteps];
    [next[i], next[j]] = [next[j], next[i]];
    manualSteps = next;
  }

  async function buildChain() {
    if (!audienceId || !dealId) return;
    buildLoading = true; errorMsg = null;
    try {
      const payload = {
        workspace_id: workspace.id,
        audience_id: audienceId,
        deal_id: dealId
      };
      if (manualSteps.length) payload.manual_steps = manualSteps;
      const r = await callBbAi('build_chain_between', payload);
      if (r?.error) throw new Error(r.detail?.message || r.error);
      await refreshWorkspace();
      showToast('Chain built', 'success');
      onChainCreated?.(r.chain_id);
      onClose?.();
    } catch (e) {
      errorMsg = e.message;
      showToast('Build failed: ' + e.message, 'error');
    } finally {
      buildLoading = false;
    }
  }
</script>

<div class="cb-overlay" onclick={onClose}>
  <div class="cb-modal" onclick={(e) => e.stopPropagation()}>
    <div class="cb-header">
      <div>
        <div class="cb-eyebrow">Chain Builder · Step {step} of 3</div>
        <h3>
          {#if step === 1}Pick the first segment
          {:else if step === 2}Pick the {audienceId ? 'offer' : 'audience'}
          {:else}Build the chain
          {/if}
        </h3>
      </div>
      <button class="cb-close" onclick={onClose} type="button">×</button>
    </div>

    {#if errorMsg}
      <div class="cb-error">{errorMsg}</div>
    {/if}

    <div class="cb-body">

      {#if step === 1}
        <div class="cb-two-col">
          <div class="cb-col">
            <div class="cb-col-title">Audiences</div>
            {#if workspace.audiences.length === 0}
              <p class="cb-empty">No audiences in your library yet.</p>
            {:else}
              <div class="cb-cards">
                {#each workspace.audiences as a}
                  <button class="cb-card cb-card-aud" onclick={() => pickAudience(a.id)} type="button">
                    <span class="cb-card-eyebrow">Audience</span>
                    <strong>{a.name || 'Untitled'}</strong>
                    {#if a.want}<span class="cb-card-sub">{a.want}</span>{/if}
                  </button>
                {/each}
              </div>
            {/if}
          </div>
          <div class="cb-col">
            <div class="cb-col-title">Offers</div>
            {#if workspace.deals.length === 0}
              <p class="cb-empty">No offers in your library yet.</p>
            {:else}
              <div class="cb-cards">
                {#each workspace.deals as d}
                  <button class="cb-card cb-card-deal" onclick={() => pickDeal(d.id)} type="button">
                    <span class="cb-card-eyebrow">Offer</span>
                    <strong>{d.name || 'Untitled'}</strong>
                    {#if d.what_it_is}<span class="cb-card-sub">{d.what_it_is}</span>{/if}
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        </div>
      {/if}

      {#if step === 2}
        <div class="cb-picked">
          <div class="cb-picked-label">Picked</div>
          {#if audience}
            <div class="cb-pill cb-pill-aud">◉ Audience · {audience.name}</div>
          {/if}
          {#if deal}
            <div class="cb-pill cb-pill-deal">◈ Offer · {deal.name}</div>
          {/if}
        </div>

        <div class="cb-or-row">
          <button class="cb-primary" onclick={aiGenerateOtherSide} disabled={pairLoading} type="button">
            {pairLoading ? 'Generating…' : `⚡ AI generate the ${audienceId ? 'offer' : 'audience'}`}
          </button>
          <span class="cb-or">or pick one below</span>
        </div>

        <div class="cb-cards">
          {#if audienceId}
            {#if workspace.deals.length === 0}
              <p class="cb-empty">No offers in your library yet — generate one above.</p>
            {:else}
              {#each workspace.deals as d}
                <button class="cb-card cb-card-deal" onclick={() => pickDeal(d.id)} type="button">
                  <span class="cb-card-eyebrow">Offer</span>
                  <strong>{d.name || 'Untitled'}</strong>
                  {#if d.what_it_is}<span class="cb-card-sub">{d.what_it_is}</span>{/if}
                </button>
              {/each}
            {/if}
          {:else}
            {#if workspace.audiences.length === 0}
              <p class="cb-empty">No audiences in your library yet — generate one above.</p>
            {:else}
              {#each workspace.audiences as a}
                <button class="cb-card cb-card-aud" onclick={() => pickAudience(a.id)} type="button">
                  <span class="cb-card-eyebrow">Audience</span>
                  <strong>{a.name || 'Untitled'}</strong>
                  {#if a.want}<span class="cb-card-sub">{a.want}</span>{/if}
                </button>
              {/each}
            {/if}
          {/if}
        </div>
      {/if}

      {#if step === 3}
        <div class="cb-picked">
          <div class="cb-pill cb-pill-aud">◉ Audience · {audience?.name || '—'}</div>
          <span class="cb-arrow">→</span>
          <div class="cb-pill cb-pill-deal">◈ Offer · {deal?.name || '—'}</div>
        </div>

        <div class="cb-section">
          <div class="cb-section-title">Optional manual steps (AI fills around them)</div>

          {#if manualSteps.length > 0}
            <ul class="cb-step-list">
              {#each manualSteps as s, i}
                <li class="cb-step-row">
                  <span class="cb-step-num">{i + 1}</span>
                  <div class="cb-step-meat">
                    <strong>{s.name}</strong>
                    {#if s.existing_id}<span class="cb-step-existing-badge">existing</span>{/if}
                    {#if s.description}<span class="cb-step-desc">{s.description}</span>{/if}
                  </div>
                  <div class="cb-step-actions">
                    <button onclick={() => moveStep(i, -1)} disabled={i === 0} type="button" title="Move up">↑</button>
                    <button onclick={() => moveStep(i, 1)} disabled={i === manualSteps.length - 1} type="button" title="Move down">↓</button>
                    <button onclick={() => removeStep(i)} type="button" title="Remove">×</button>
                  </div>
                </li>
              {/each}
            </ul>
          {/if}

          <div class="cb-step-add">
            <input
              bind:value={stepDraft.name}
              placeholder="Step title (e.g. Sign up, First call)"
              onkeydown={(e) => { if (e.key === 'Enter' && stepDraft.name.trim()) { e.preventDefault(); addManualStep(); } }}
            />
            <input
              bind:value={stepDraft.description}
              placeholder="Optional: what happens here"
            />
            <button onclick={addManualStep} disabled={!stepDraft.name.trim()} type="button">+ Add</button>
          </div>

          {#if availableExistingSteps.length > 0}
            <div class="cb-step-existing">
              <span class="cb-step-existing-label">Or pull from your library:</span>
              <select bind:value={existingStepId}>
                <option value="">— Pick an existing step —</option>
                {#each availableExistingSteps as s}
                  <option value={s.id}>{s.step_name || 'Untitled step'}</option>
                {/each}
              </select>
              <button onclick={addExistingStep} disabled={!existingStepId} type="button">+ Add existing</button>
            </div>
          {/if}
        </div>

        <div class="cb-actions">
          <button class="cb-primary" onclick={buildChain} disabled={buildLoading} type="button">
            {buildLoading ? 'Building chain…' : '⚡ Build chain'}
          </button>
          <button class="cb-secondary" onclick={onClose} type="button">Cancel</button>
        </div>
      {/if}

    </div>
  </div>
</div>

<style>
  .cb-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 950;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: cbFadeIn 0.18s ease;
  }
  @keyframes cbFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .cb-modal {
    background: linear-gradient(180deg, rgba(20,18,22,0.97), rgba(12,11,14,0.97));
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 18px;
    width: 100%;
    max-width: 880px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05);
    overflow: hidden;
    animation: cbModalIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes cbModalIn {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .cb-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 22px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .cb-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    color: #d4af37;
    margin-bottom: 4px;
  }
  .cb-header h3 {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    font-weight: 400;
    margin: 0;
    color: #f0efe9;
    letter-spacing: -0.01em;
  }
  .cb-close {
    background: transparent; border: none;
    color: #888; font-size: 24px; cursor: pointer;
    padding: 0 8px; line-height: 1;
    margin: 0;
  }
  .cb-close:hover { color: #fff; }

  .cb-error {
    margin: 12px 24px 0;
    padding: 10px 12px;
    background: rgba(212,100,100,0.08);
    border: 1px solid rgba(212,100,100,0.3);
    border-radius: 8px;
    color: #d48484;
    font-size: 12px;
    line-height: 1.4;
  }

  .cb-body {
    padding: 18px 24px 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .cb-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  @media (max-width: 720px) {
    .cb-two-col { grid-template-columns: 1fr; }
  }
  .cb-col-title {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: #9aa0a6;
    margin-bottom: 10px;
  }

  .cb-cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }
  .cb-card {
    text-align: left;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.08);
    color: #d8d8dc;
    padding: 12px 14px;
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    box-shadow: none;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  }
  .cb-card:hover {
    background: rgba(255,255,255,0.05);
    transform: translateY(-1px);
  }
  .cb-card-aud:hover { border-color: rgba(122,184,255,0.6); }
  .cb-card-deal:hover { border-color: rgba(212,175,55,0.6); }
  .cb-card-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: #9aa0a6;
  }
  .cb-card-aud .cb-card-eyebrow { color: #7AB8FF; }
  .cb-card-deal .cb-card-eyebrow { color: #d4af37; }
  .cb-card strong { color: #f0efe9; font-size: 14px; font-weight: 600; }
  .cb-card-sub { color: #9aa0a6; font-size: 12px; line-height: 1.4; }
  .cb-empty {
    color: #56565c;
    font-size: 13px;
    font-style: italic;
    margin: 0;
    padding: 12px 0;
  }

  .cb-picked {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    padding: 12px 14px;
  }
  .cb-picked-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: #9aa0a6;
  }
  .cb-pill {
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: rgba(255,255,255,0.04);
  }
  .cb-pill-aud { color: #7AB8FF; border: 1px solid rgba(122,184,255,0.4); }
  .cb-pill-deal { color: #d4af37; border: 1px solid rgba(212,175,55,0.4); }
  .cb-arrow { color: #d4af37; font-size: 18px; }

  .cb-or-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .cb-or {
    color: #9aa0a6;
    font-size: 12px;
    font-style: italic;
  }

  .cb-section {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    padding: 14px;
  }
  .cb-section-title {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: #9aa0a6;
    margin-bottom: 10px;
  }
  .cb-step-list {
    list-style: none;
    padding: 0;
    margin: 0 0 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .cb-step-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
  }
  .cb-step-num {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #d4af37;
    width: 18px;
    flex-shrink: 0;
  }
  .cb-step-meat {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .cb-step-meat strong { color: #f0efe9; font-size: 13px; }
  .cb-step-desc { color: #9aa0a6; font-size: 12px; line-height: 1.4; }
  .cb-step-actions { display: flex; gap: 4px; }
  .cb-step-actions button {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    color: #b8b8bc;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    cursor: pointer;
    padding: 0;
    font-size: 12px;
    margin: 0;
    box-shadow: none;
    line-height: 1;
  }
  .cb-step-actions button:hover:not(:disabled) {
    color: #d4af37;
    border-color: rgba(212,175,55,0.4);
  }
  .cb-step-actions button:disabled { opacity: 0.3; cursor: not-allowed; }

  .cb-step-add {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 6px;
  }
  .cb-step-add input {
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.08);
    color: #f4f4f6;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 13px;
    box-sizing: border-box;
    margin: 0;
    font-family: inherit;
  }
  .cb-step-add input:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .cb-step-add button {
    background: rgba(212,175,55,0.12);
    color: #d4af37;
    border: 1px solid rgba(212,175,55,0.3);
    padding: 0 14px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    margin: 0;
    box-shadow: none;
  }
  .cb-step-add button:disabled { opacity: 0.4; cursor: not-allowed; }
  .cb-step-add button:hover:not(:disabled) { background: rgba(212,175,55,0.22); }

  .cb-step-existing {
    margin-top: 10px;
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    padding: 10px 12px;
    background: rgba(181,131,240,0.06);
    border: 1px solid rgba(181,131,240,0.25);
    border-radius: 8px;
  }
  .cb-step-existing-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #B583F0;
  }
  .cb-step-existing select {
    flex: 1;
    min-width: 180px;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(181,131,240,0.3);
    color: #f4f4f6;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
  }
  .cb-step-existing select:focus { outline: none; border-color: rgba(181,131,240,0.6); }
  .cb-step-existing button {
    background: rgba(181,131,240,0.15);
    color: #B583F0;
    border: 1px solid rgba(181,131,240,0.4);
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    margin: 0;
    box-shadow: none;
  }
  .cb-step-existing button:hover:not(:disabled) { background: rgba(181,131,240,0.25); }
  .cb-step-existing button:disabled { opacity: 0.45; cursor: not-allowed; }
  .cb-step-existing-badge {
    display: inline-block;
    margin-left: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #B583F0;
    background: rgba(181,131,240,0.15);
    padding: 1px 6px;
    border-radius: 999px;
  }

  .cb-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .cb-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 10px 22px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    font-size: 13px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25), 0 6px 18px rgba(212,175,55,0.25);
    margin: 0;
  }
  .cb-primary:disabled { opacity: 0.45; cursor: not-allowed; }
  .cb-primary:hover:not(:disabled) { transform: translateY(-1px); }
  .cb-secondary {
    background: transparent;
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.12);
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    margin: 0;
    box-shadow: none;
  }
  .cb-secondary:hover { color: #fff; border-color: rgba(255,255,255,0.25); }
</style>
